import { Search, Menu, Settings, HelpCircle, Moon, Sun, LogOut, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useTheme } from '../../context/ThemeContext';
import { getInitials } from '../../lib/avatar';

const LOGO_URL = '/netlen-logo.svg';

export const AppHeader = ({
  user,
  onToggleSidebar,
  searchQuery,
  onSearchChange,
  onOpenSettings,
  onLogout,
}) => {
  const { theme, toggleTheme } = useTheme();
  const displayName = user?.display_name || user?.email?.split('@')[0] || 'Kullanıcı';

  return (
    <header
      className="h-16 shrink-0 flex items-center gap-3 px-3 sm:px-4 border-b border-border bg-background"
      data-testid="app-header"
    >
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleSidebar}
          aria-label="Menüyü aç/kapat"
          className="h-10 w-10 rounded-lg"
          data-testid="sidebar-toggle"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="Netlen"
            className="h-7 w-auto dark:invert"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className="hidden sm:block h-6 w-px bg-border" />
          <span className="hidden sm:block text-base text-muted-foreground">Webmail</span>
        </div>
      </div>

      {/* Genel arama: mail listesini arar */}
      <div className="flex-1 flex justify-center min-w-0">
        <div className="relative w-full max-w-2xl">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="E-postalarda ara..."
            aria-label="E-postalarda ara"
            className="w-full h-10 pl-10 pr-9 rounded-lg bg-secondary border border-transparent text-sm placeholder:text-muted-foreground focus:outline-none focus:bg-background focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-colors"
            data-testid="mail-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              aria-label="Aramayı temizle"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onOpenSettings('profile')}
          aria-label="Ayarlar"
          className="hidden sm:inline-flex h-10 w-10 rounded-full"
          data-testid="header-settings"
        >
          <Settings className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onOpenSettings('help')}
          aria-label="Yardım"
          className="hidden sm:inline-flex h-10 w-10 rounded-full"
          data-testid="header-help"
        >
          <HelpCircle className="h-5 w-5" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="ml-1 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Hesap menüsü"
              data-testid="user-menu-trigger"
            >
              <Avatar className="h-9 w-9">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="Avatar" className="h-full w-full object-cover rounded-full" />
                ) : (
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                    {getInitials(user?.display_name, user?.email)}
                  </AvatarFallback>
                )}
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel className="font-normal">
              <p className="text-sm font-medium truncate">{displayName}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
              <span>{theme === 'dark' ? 'Açık tema' : 'Koyu tema'}</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onOpenSettings('profile')} data-testid="settings-menu">
              <Settings className="h-4 w-4 mr-2" />
              <span>Ayarlar</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} className="text-destructive" data-testid="logout-button">
              <LogOut className="h-4 w-4 mr-2" />
              <span>Çıkış yap</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default AppHeader;
